import React, { useState, useEffect } from 'react';
import { X, Save, Plus } from 'lucide-react';
import api from '../../../../utils/api';

const AVAILABLE_LEAD_FIELDS = [
  { value: 'name', label: 'Lead Name' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'email', label: 'Email Address' },
  { value: 'status', label: 'Lead Status' },
  { value: 'leadType', label: 'Lead Type' },
  { value: 'source', label: 'Lead Source' },
  { value: 'location', label: 'Location' },
  { value: 'propertyInterest', label: 'Property Interest' },
  { value: 'budget', label: 'Budget' },
  { value: 'assignedTelecallerName', label: 'Telecaller Name' },
  { value: 'assignedExecutiveName', label: 'Executive Name' }
];

const WWebTemplateFormModal = ({ isOpen, onClose, templateToEdit, onSave }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Promotional');
  const [body, setBody] = useState('');
  const [detectedVariables, setDetectedVariables] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (templateToEdit) {
      setName(templateToEdit.name);
      setCategory(templateToEdit.category);
      setBody(templateToEdit.body);
      setDetectedVariables(templateToEdit.variables || []);
    } else {
      setName('');
      setCategory('Promotional');
      setBody('');
      setDetectedVariables([]);
    }
  }, [templateToEdit, isOpen]);

  useEffect(() => {
    // Auto-detect variables on body change
    const regex = /\{\{(\w+)\}\}/g;
    const variables = [];
    let match;
    const seen = new Set();
    while ((match = regex.exec(body)) !== null) {
      if (!seen.has(match[1])) {
        seen.add(match[1]);
        variables.push({
          placeholder: match[1],
          label: match[1].charAt(0).toUpperCase() + match[1].slice(1),
          defaultSource: 'lead_field'
        });
      }
    }
    setDetectedVariables(variables);
  }, [body]);

  const insertVariable = (field) => {
    setBody(prev => prev + `{{${field}}}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const url = templateToEdit ? `/wa-web-templates/${templateToEdit._id}` : '/wa-web-templates';
      const method = templateToEdit ? 'PUT' : 'POST';
      
      const res = await api(url, {
        method,
        body: { name, category, body }
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to save template');
      }
      
      const savedTemplate = await res.json();
      onSave(savedTemplate);
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>{templateToEdit ? 'Edit Template' : 'Create New Template'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Template Name</label>
            <input 
              type="text" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              required
              placeholder="e.g. Follow-up 1, New Project Launch"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Category</label>
            <select 
              value={category} 
              onChange={e => setCategory(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }}
            >
              <option value="Promotional">Promotional</option>
              <option value="Follow-up">Follow-up</option>
              <option value="Reminder">Reminder</option>
              <option value="Welcome">Welcome</option>
              <option value="Closing">Closing</option>
              <option value="Site Visit">Site Visit</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontWeight: '500' }}>Message Body</label>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <select id="var-select" style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', fontSize: '12px' }}>
                  {AVAILABLE_LEAD_FIELDS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <button 
                  type="button" 
                  onClick={() => insertVariable(document.getElementById('var-select').value)}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--color-background-tertiary)', border: '1px solid var(--color-border-primary)', borderRadius: '4px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}
                >
                  <Plus size={14} /> Insert
                </button>
              </div>
            </div>
            
            <textarea 
              value={body} 
              onChange={e => setBody(e.target.value)} 
              required
              rows={6}
              placeholder="Hello {{name}}, your property search for {{location}}..."
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', resize: 'vertical' }}
            />
            
            <div style={{ marginTop: '12px', padding: '12px', background: 'var(--color-background-secondary)', borderRadius: '6px', fontSize: '13px' }}>
              <strong>Detected Variables: </strong>
              {detectedVariables.length === 0 ? (
                <span style={{ color: 'var(--color-text-tertiary)' }}>None</span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {detectedVariables.map(v => (
                    <span key={v.placeholder} style={{ background: 'var(--color-brand-primary)', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                      {`{{${v.placeholder}}}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <button 
              type="button" 
              onClick={onClose}
              style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', background: 'white', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '6px', border: 'none', background: 'var(--color-brand-primary)', color: 'white', cursor: isSubmitting ? 'wait' : 'pointer' }}
            >
              <Save size={16} /> {isSubmitting ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WWebTemplateFormModal;
